import React, { useState, useCallback, useMemo } from 'react'
import { Button, IconButton, Popover, TextField } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ArrowForwardIos, Close } from '@mui/icons-material'
import { DateRange, DateRangeProps } from 'react-date-range'
import { format } from 'date-fns'
import { useDiagnosis } from 'data/treatments'
import { Treatment } from 'types/treatments'

const statusList = ['pending', 'ongoing', 'completed', 'cancelled'] as const
const INITIAL_ITEMS_TO_SHOW = 50
const ITEMS_PER_LOAD = 25

interface FilterProps {
  filterByDate: (data: DateRangeProps['ranges'] | null) => void
  filterByDiagnosis: (diagnosis: string | null) => void
  filterByStatus: (status: string | null) => void
  dateRange: DateRangeProps['ranges'] | undefined
  diagnosis: string | undefined
  status: Treatment['treatment_status'] | undefined
}

interface FilterState {
  selectionRange: DateRangeProps['ranges'] | null
  diagnosis: string | null
  status: Treatment['treatment_status'] | null
}

interface DiagnosisItem {
  id: string
  diagnosis_name: string
}

const SearchableList: React.FC<{
  items: DiagnosisItem[]
  onSelect: (item: DiagnosisItem) => void
}> = ({ items, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS_TO_SHOW)
  
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return items
    
    const term = searchTerm.toLowerCase()
    return items.filter(item => 
      item.diagnosis_name.toLowerCase().includes(term)
    )
  }, [items, searchTerm])

  const displayedItems = useMemo(() => {
    return filteredItems.slice(0, visibleCount)
  }, [filteredItems, visibleCount])

  const hasMore = filteredItems.length > visibleCount

  const handleItemClick = useCallback((item: DiagnosisItem) => {
    onSelect(item)
  }, [onSelect])

  const handleLoadMore = useCallback(() => {
    setVisibleCount(prev => Math.min(prev + ITEMS_PER_LOAD, filteredItems.length))
  }, [filteredItems.length])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setVisibleCount(INITIAL_ITEMS_TO_SHOW)
  }, [])

  return (
    <div className="w-80 max-w-sm max-h-96 flex flex-col bg-white shadow-default rounded-xl">
      <div className="p-3 border-b bg-white sticky top-0 z-10 rounded-t-xl">
        <TextField
          fullWidth
          size="small"
          placeholder="Search diagnoses..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            className:"pl-2",
            startAdornment: (
              <div className="mr-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            )
          }}
          className="bg-gray-50"
        />
      </div>
      
      <div className="flex-1 overflow-auto p-2">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            {searchTerm ? 'No results found' : 'No diagnoses available'}
          </div>
        ) : (
          <>
            <div className="space-y-1">
              {displayedItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="w-full text-left px-3 py-2 text-sm text-neutral-700 hover:bg-gray-50 rounded-lg transition-colors duration-150 border border-transparent hover:border-gray-200"
                >
                  {item.diagnosis_name}
                </button>
              ))}
            </div>
            
            {hasMore && (
              <div className="pt-2 mt-2 border-t">
                <button
                  onClick={handleLoadMore}
                  className="w-full text-center py-2 text-sm text-brand-600 hover:bg-brand-50 rounded-lg transition-colors duration-150"
                >
                  Load More ({filteredItems.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default function Filter(props: FilterProps) {
  const { filterByDate, filterByDiagnosis, filterByStatus } = props
  
  const [filters, setFilters] = useState<FilterState>({
    selectionRange: props.dateRange || null,
    diagnosis: props.diagnosis || null,
    status: props.status || null,
  })

  const { selectionRange, diagnosis, status } = filters
  const [openFilterBox, setOpenFilterBox] = useState<HTMLButtonElement | null>(null)
  const [dateFilterOpen, setDateFilterOpen] = useState<HTMLButtonElement | null>(null)
  const [diagnosisFilterOpen, setDiagnosisFilterOpen] = useState<HTMLButtonElement | null>(null)
  const [statusFilterOpen, setStatusFilterOpen] = useState<HTMLButtonElement | null>(null)
  
  const { data: diagnoses = [] } = useDiagnosis()

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenFilterBox(event.currentTarget)
  }, [])

  const resetFilter = useCallback(() => {
    setFilters({
      selectionRange: null,
      diagnosis: null,
      status: null,
    })
    filterByDate(null)
    filterByDiagnosis(null)
    filterByStatus(null)
    setOpenFilterBox(null)
  }, [filterByDate, filterByDiagnosis, filterByStatus])

  const filterFunction = useCallback((
    name: 'selectionRange' | 'diagnosis' | 'status',
    item: any
  ) => {
    const newFilters = {
      selectionRange: null,
      diagnosis: null,
      status: null,
    }
    newFilters[name] = item
    setFilters(newFilters)
  }, [])

  const label = useMemo(() => {
    if (selectionRange && selectionRange[0].startDate && selectionRange[0].endDate) {
      return (
        <div className="bg-brand-50 text-brand-700 rounded-lg p-2">
          {selectionRange?.[0].startDate === selectionRange?.[0].endDate
            ? format(selectionRange?.[0].startDate, 'dd/MM/yyyy')
            : `${format(selectionRange?.[0].startDate, 'dd/MM/yyyy')}-${format(
                selectionRange?.[0].endDate,
                'dd/MM/yyyy'
              )}`}
        </div>
      )
    } else if (diagnosis) {
      return <div className="bg-brand-50 text-brand-700 rounded-lg p-2 h-fit">{diagnosis}</div>
    } else if (status) {
      return <div className="bg-brand-50 text-brand-700 rounded-lg p-2 h-fit">{status}</div>
    }
    return 'Filter'
  }, [selectionRange, diagnosis, status])

  const handleDiagnosisSelect = useCallback((item: DiagnosisItem) => {
    resetFilter()
    filterFunction('diagnosis', item.diagnosis_name)
    filterByDiagnosis(item.diagnosis_name)
  }, [resetFilter, filterFunction, filterByDiagnosis])

  return (
    <>
      <div className="flex items-center">
        <Button
          startIcon={<FilterListIcon />}
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          className="px-3 border border-default text-neutral-500 w-fit h-fit py-3"
          onClick={handleClick}
        >
          {label}
        </Button>
        {(selectionRange || diagnosis || status) && (
          <IconButton
            className="w-fit h-fit ml-2"
            onClick={resetFilter}
            aria-label="Clear applied filter"
          >
            <Close />
          </IconButton>
        )}
      </div>
      
      <Popover
        open={Boolean(openFilterBox)}
        anchorEl={openFilterBox}
        onClose={() => setOpenFilterBox(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="rounded-xl bg-white w-full flex flex-col justify-center items-start rounded-10 bg-neutral-color-white shadow-default">
          <Button
            fullWidth
            size="small"
            onClick={(e) => {
              setDateFilterOpen(e.currentTarget)
            }}
            endIcon={<ArrowForwardIos className="text-xs" />}
            variant="text"
            className="hover:bg-white text-neutral-500 h-fit justify-start px-4"
          >
            Date
          </Button>
          <Button
            fullWidth
            size="small"
            onClick={(e) => {
              setDiagnosisFilterOpen(e.currentTarget)
            }}
            endIcon={<ArrowForwardIos className="text-xs" />}
            variant="text"
            className="hover:bg-white text-neutral-500 h-fit justify-start px-4"
          >
            Diagnosis
          </Button>
          <Button
            fullWidth
            size="small"
            onClick={(e) => {
              setStatusFilterOpen(e.currentTarget)
            }}
            endIcon={<ArrowForwardIos className="text-xs" />}
            variant="text"
            className="hover:bg-white text-neutral-500 h-fit justify-start px-4"
          >
            Status
          </Button>
        </div>
      </Popover>

      <Popover
        open={Boolean(dateFilterOpen)}
        anchorEl={dateFilterOpen}
        onClose={() => {
          setDateFilterOpen(null)
        }}
      >
        <DateRange
          onChange={(item) => {
            resetFilter()
            filterFunction('selectionRange', [item.selection])
            filterByDate([item.selection])
          }}
          moveRangeOnFirstSelection={false}
          ranges={
            filters.selectionRange || [
              {
                startDate: new Date(),
                endDate: new Date(),
                key: 'selection',
              },
            ]
          }
          showDateDisplay={false}
          rangeColors={['#FFFFFF']}
          color="#000000"
          className="date-range-picker rounded-xl"
        />
      </Popover>

      <Popover
        open={Boolean(diagnosisFilterOpen)}
        anchorEl={diagnosisFilterOpen}
        onClose={() => {
          setDiagnosisFilterOpen(null)
        }}
      >
        <SearchableList
          items={diagnoses}
          onSelect={handleDiagnosisSelect}
        />
      </Popover>

      <Popover
        open={Boolean(statusFilterOpen)}
        anchorEl={statusFilterOpen}
        onClose={() => {
          setStatusFilterOpen(null)
        }}
      >
        <div className="rounded-xl overflow-auto gap-2 p-2 bg-white flex flex-col justify-center items-start rounded-10 shadow-default">
          {statusList?.map((item) => (
            <Button
              fullWidth
              key={item}
              className="text-neutral-500 border border-neutral-100 h-fit"
              variant="outlined"
              size="large"
              onClick={() => {
                resetFilter()
                filterFunction('status', item)
                filterByStatus(item)
              }}
            >
              {item}
            </Button>
          ))}
        </div>
      </Popover>
    </>
  )
}
